import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { AiKeyMissingNotice } from './AiKeyBanner.jsx';
import { Modal } from './ui';
import { extractUrls } from '../util/extractUrls.js';

// T141 — Batch import modal.
//
// Operator drops in a document / paste of many saved job links. We detect the
// http(s) URLs client-side (preview), POST them, then poll the JobImport row
// for live progress while the worker fetches + Claude-extracts + scores each
// one through the full evaluation pipeline. Imported jobs land in the
// operator's "Available / New" pile (or Shortlisted, if chosen) with scores +
// AI explanations, exactly like a daily scan would produce.

const MAX_URLS = 100;

export default function ImportJobsModal({ onClose, onCreated }) {
  const { user } = useAuth();
  const hasAnthropicKey = !!user?.connectors?.anthropicKey;

  const [rawText, setRawText] = useState('');
  const [fileName, setFileName] = useState('');
  const [landingStatus, setLandingStatus] = useState('new'); // 'new' | 'shortlisted'
  const [explain, setExplain] = useState(true); // generate AI "why match" per job

  const [status, setStatus] = useState('idle'); // idle|submitting|running|completed|failed
  const [importId, setImportId] = useState(null);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const urls = extractUrls(rawText, { max: MAX_URLS });
  const tooMany = extractUrls(rawText, { max: MAX_URLS + 1 }).length > MAX_URLS;

  // Poll the import row for live progress until it reaches a terminal state.
  useEffect(() => {
    if (!importId) return undefined;
    let alive = true;
    let timer;
    const tick = async () => {
      try {
        const r = await api.get(`/jobs/import/${importId}`);
        if (!alive) return;
        setProgress(r);
        if (r.status === 'completed' || r.status === 'failed') {
          setStatus(r.status);
          return; // stop polling
        }
      } catch {
        /* transient — keep trying */
      }
      if (alive) timer = setTimeout(tick, 1500);
    };
    timer = setTimeout(tick, 700);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [importId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setFileName(file.name);
      // Merge file content into the textarea so the operator sees what was loaded.
      setRawText((prev) => (prev.trim() ? `${prev}\n${text}` : text));
    } catch {
      toast('Could not read that file', { kind: 'error' });
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const start = async () => {
    if (urls.length === 0) {
      toast('Paste or upload some job links first', { kind: 'info' });
      return;
    }
    setStatus('submitting');
    try {
      const r = await api.post('/jobs/import', { urls, landingStatus, explain });
      setImportId(r.importId);
      setStatus('running');
      setProgress({
        status: r.status ?? 'running',
        totalUrls: r.totalUrls ?? urls.length,
        processed: 0,
        created: 0,
        updated: 0,
        failed: 0,
        results: [],
      });
      if (r.alreadyRunning) toast('An import is already running — showing its progress', { kind: 'info' });
    } catch (e) {
      setStatus('idle');
      const code = e.body?.error;
      let msg = e.body?.message || e.message;
      if (code === 'anthropic_key_missing') msg = 'Set your Anthropic API key in Settings → Connectors first.';
      toast(msg, { kind: 'error' });
    }
  };

  // Refresh the dashboard if anything landed, then close.
  const finishAndClose = () => {
    if ((progress?.created ?? 0) > 0 || (progress?.updated ?? 0) > 0) onCreated?.(progress);
    onClose?.();
  };

  const running = status === 'running' || status === 'submitting';
  const done = status === 'completed' || status === 'failed';

  return (
    <Modal role="dialog" size="lg" scroll labelledById="import-jobs-title" onClose={importId ? finishAndClose : onClose}>
      <header className="flex items-start justify-between gap-3 border-b border-slate-800 p-4">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-brand-500">Import</p>
          <h2 id="import-jobs-title" className="mt-0.5 text-base font-semibold text-slate-100">
            Import jobs from a list of links
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Paste or upload a document of saved job links. Each one is fetched, parsed by Claude, scored, and lands in
            your board — same pipeline as a daily scan.
          </p>
        </div>
        <button
          type="button"
          onClick={importId ? finishAndClose : onClose}
          aria-label="Close"
          className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-200"
        >
          ✕
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        {!hasAnthropicKey && status === 'idle' && (
          <div className="mb-4">
            <AiKeyMissingNotice feature="Batch import" />
          </div>
        )}

        {status === 'idle' && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[10px] uppercase tracking-wide text-slate-400">Job links</label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".txt,.csv,.md,.tsv,text/plain,text/csv,text/markdown"
                    onChange={onFile}
                    className="hidden"
                    id="import-file"
                  />
                  <label
                    htmlFor="import-file"
                    className="cursor-pointer rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 transition hover:border-brand-600 hover:text-brand-400"
                  >
                    ⬆ Upload file
                  </label>
                </div>
              </div>
              <textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                rows={9}
                placeholder={'Paste your saved job links — one per line or anywhere in the text:\nhttps://example.dk/careers/senior-engineer\nhttps://www.linkedin.com/jobs/view/123456789\n…'}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm leading-relaxed text-slate-100 focus:border-brand-500 focus:outline-none"
              />
              <div className="mt-1 flex items-center justify-between text-xs">
                <span className={urls.length > 0 ? 'text-brand-400' : 'text-slate-500'}>
                  {urls.length > 0
                    ? `${urls.length} link${urls.length === 1 ? '' : 's'} detected`
                    : 'No links detected yet'}
                  {fileName && <span className="text-slate-500"> · from {fileName}</span>}
                </span>
                {tooMany && <span className="text-amber-400">capped at {MAX_URLS} per import</span>}
              </div>
              {urls.length > 0 && (
                <ul className="mt-2 max-h-24 overflow-y-auto rounded-md border border-slate-800 bg-slate-950/30 p-2 text-[11px] text-slate-400">
                  {urls.slice(0, 8).map((u) => (
                    <li key={u} className="truncate">{u}</li>
                  ))}
                  {urls.length > 8 && <li className="text-slate-500">+{urls.length - 8} more…</li>}
                </ul>
              )}
            </div>

            {/* Options */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wide text-slate-400">Land in</span>
                <div className="flex rounded-md border border-slate-700 p-0.5">
                  {[
                    { value: 'new', label: 'New (triage)' },
                    { value: 'shortlisted', label: 'Shortlisted' },
                  ].map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setLandingStatus(o.value)}
                      className={
                        'rounded px-2 py-0.5 text-xs transition ' +
                        (landingStatus === o.value ? 'bg-brand-600/15 text-brand-500' : 'text-slate-400 hover:text-slate-200')
                      }
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-300">
                <input
                  type="checkbox"
                  checked={explain}
                  onChange={(e) => setExplain(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-950/40 text-brand-500 focus:ring-1 focus:ring-brand-500"
                />
                Generate AI explanations <span className="text-slate-500">(uses tokens)</span>
              </label>
            </div>
          </div>
        )}

        {(running || done) && progress && <ImportProgress progress={progress} done={done} />}
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-slate-800 bg-slate-900/60 p-3">
        {status === 'idle' ? (
          <>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-400 hover:border-slate-500 hover:text-slate-200"
            >
              Cancel
            </button>
            {hasAnthropicKey && (
              <button
                type="button"
                onClick={start}
                disabled={urls.length === 0}
                className="rounded-md bg-brand-600 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-500 disabled:opacity-50"
              >
                {urls.length > 0 ? `✨ Import ${urls.length} job${urls.length === 1 ? '' : 's'}` : '✨ Import'}
              </button>
            )}
          </>
        ) : done ? (
          <button
            type="button"
            onClick={finishAndClose}
            className="rounded-md bg-brand-600 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-500"
          >
            Done → view in dashboard
          </button>
        ) : (
          <button
            type="button"
            onClick={finishAndClose}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
            title="Keep importing in the background and return to the dashboard"
          >
            Run in background
          </button>
        )}
      </footer>
    </Modal>
  );
}

// Live progress: a bar + counters + a scrollable per-URL result list.
function ImportProgress({ progress, done }) {
  const total = progress.totalUrls || 0;
  const processed = progress.processed || 0;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const results = progress.results ?? [];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-300">
          {done ? (progress.status === 'failed' ? '⚠ Import failed' : '✓ Import complete') : `Importing… ${processed}/${total}`}
        </span>
        <span className="text-slate-500">{pct}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
        <div
          className={'h-full rounded-full transition-all ' + (progress.status === 'failed' ? 'bg-red-600' : 'bg-brand-600')}
          style={{ width: `${Math.max(pct, processed > 0 ? 3 : 0)}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        <Counter label="new" value={progress.created} tone="emerald" />
        <Counter label="updated" value={progress.updated} tone="slate" />
        <Counter label="failed" value={progress.failed} tone="red" />
        {progress.semanticScored > 0 && <Counter label="semantic" value={progress.semanticScored} tone="slate" />}
        {progress.explained > 0 && <Counter label="✨ explained" value={progress.explained} tone="brand" />}
      </div>
      {progress.fatalError && (
        <p className="rounded-lg border border-red-800 bg-red-950/20 p-2 text-xs text-red-300">{progress.fatalError}</p>
      )}
      {results.length > 0 && (
        <ul className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-slate-800 bg-slate-950/30 p-2 text-[11px]">
          {results.map((r, i) => (
            <li key={`${r.url}-${i}`} className="flex items-start gap-2">
              <span aria-hidden="true" className="shrink-0">
                {r.status === 'failed' ? '⚠' : r.status === 'created' ? '✓' : '↻'}
              </span>
              <span className="min-w-0 flex-1 truncate text-slate-300" title={r.url}>
                {r.title ? (
                  <>
                    <span className="text-slate-100">{r.title}</span>
                    {r.company && <span className="text-slate-500"> @ {r.company}</span>}
                  </>
                ) : (
                  <span className="text-slate-500">{r.url}</span>
                )}
                {r.status === 'failed' && r.error && <span className="text-red-400"> · {r.error}</span>}
              </span>
              {typeof r.score === 'number' && (
                <span className="shrink-0 font-mono text-slate-400">{r.score}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {!done && (
        <p className="text-[11px] text-slate-500">
          You can close this and keep working — the import continues, and it lands in your activity feed
          when it finishes. Refresh the board then to see the new jobs.
        </p>
      )}
    </div>
  );
}

function Counter({ label, value, tone }) {
  const tones = {
    emerald: 'text-emerald-400',
    red: 'text-red-400',
    brand: 'text-brand-400',
    slate: 'text-slate-400',
  };
  return (
    <span className="text-slate-500">
      <span className={`font-mono font-medium ${tones[tone] ?? 'text-slate-400'}`}>{value ?? 0}</span> {label}
    </span>
  );
}
